import { useMemo, useState } from "react";
import { Users, Search, Upload, UserPlus, ShieldAlert, X, Ban } from "lucide-react";
import { fone } from "./api";

const ABAS = [
  { id: "base", rotulo: "Base de clientes", icone: Users },
  { id: "csv", rotulo: "Importar CSV", icone: Upload },
  { id: "manual", rotulo: "Adicionar manual", icone: UserPlus },
];

/**
 * Escolha de quem recebe: base propria, CSV ou digitado na hora.
 *
 * Base propria e' onde o consentimento e' rastreavel. CSV e manual trazem
 * numeros de fora, entao exigem que a loja declare ter autorizacao — a multa
 * da LGPD chega em quem disparou, e sem esse registro nao ha como mostrar quem
 * autorizou. A trava fica no backend tambem; aqui e' so' a porta.
 */
export default function Destinatarios({
  publico, bloqueados = [], segmentos, selecionados, setSelecionados,
  avulsos, setAvulsos, consentimento, setConsentimento, funil,
}) {
  const [aba, setAba] = useState("base");
  const [filtros, setFiltros] = useState([]);
  const [busca, setBusca] = useState("");
  const [rascunho, setRascunho] = useState("");

  const filtrar = (itens, termo) => itens
    .filter((p) => !filtros.length || filtros.includes(p.segmento))
    .filter((p) => !termo || (p.nome || "").toLowerCase().includes(termo) || p.telefone.includes(termo));
  const termo = busca.trim().toLowerCase();
  const lista = useMemo(() => filtrar(publico, termo),
    [publico, filtros, busca]);  // eslint-disable-line react-hooks/exhaustive-deps
  // Quem pediu PARE (ou bateu no teto semanal) aparece BLOQUEADO no fim da
  // lista em vez de sumir — visivel, com o motivo, e impossivel de marcar.
  const listaBloqueados = useMemo(() => filtrar(bloqueados, termo),
    [bloqueados, filtros, busca]);  // eslint-disable-line react-hooks/exhaustive-deps

  const todosMarcados = lista.length > 0 && lista.every((p) => selecionados.has(p.cliente_id));
  const alternarTodos = () => setSelecionados((s) => {
    const n = new Set(s);
    lista.forEach((p) => todosMarcados ? n.delete(p.cliente_id) : n.add(p.cliente_id));
    return n;
  });

  const adicionarNumeros = (texto) => {
    const novos = texto.split(/[\s,;\n]+/).map((t) => t.trim()).filter(Boolean);
    if (novos.length) setAvulsos((a) => [...new Set([...a, ...novos])]);
    setRascunho("");
  };

  const lerCsv = (arquivo) => {
    const leitor = new FileReader();
    leitor.onload = (e) => {
      // Linha a linha, qualquer coluna — planilha de lojista raramente tem
      // cabecalho previsivel. A classe NAO pode ter \s: \s casa quebra de
      // linha e o arquivo inteiro virava UM "telefone" gigante (bug real:
      // importava 1 numero de um CSV com centenas).
      const achados = [];
      for (const linha of String(e.target.result || "").split(/\r?\n/)) {
        for (const m of linha.match(/\d[\d ().-]{7,}\d/g) || []) {
          const dig = m.replace(/\D/g, "");
          // 10-13 digitos: DDD+fixo ate' 55+DDD+celular. Fora disso e' CPF,
          // CNPJ, data colada ou lixo — nao entra.
          if (dig.length >= 10 && dig.length <= 13) achados.push(dig);
        }
      }
      if (!achados.length) { window.alert("Não encontrei telefones nesse arquivo."); return; }
      setAvulsos((a) => [...new Set([...a, ...achados])]);
    };
    leitor.readAsText(arquivo);
  };

  const precisaConsentimento = avulsos.length > 0;

  return (
    <div className="rounded-xl border bg-card p-5">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <span className="rounded-lg bg-violet-500/10 text-violet-500 p-2"><Users className="w-4 h-4" /></span>
          <div>
            <h3 className="font-semibold">Destinatários</h3>
            <p className="text-xs text-muted-foreground">Selecione quem vai receber</p>
          </div>
        </div>
        {(selecionados.size > 0 || avulsos.length > 0) && (
          <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-semibold tabular-nums">
            {selecionados.size + avulsos.length} selecionado{selecionados.size + avulsos.length > 1 ? "s" : ""}
            <button onClick={() => { setSelecionados(new Set()); setAvulsos([]); }}
              className="hover:underline font-normal">limpar</button>
          </span>
        )}
      </div>

      <div className="grid grid-cols-3 gap-1 rounded-lg border p-1 mb-4">
        {ABAS.map(({ id, rotulo, icone: Icone }) => (
          <button key={id} onClick={() => setAba(id)}
            className={`inline-flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
              aba === id ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
            <Icone className="w-3.5 h-3.5" /> <span className="truncate">{rotulo}</span>
          </button>
        ))}
      </div>

      {aba === "base" && (
        <>
          {/* Mesmas separacoes da tela Pessoas e Fornecedores -> Clientes, mais
              TODOS. A regra e' a mesma do backend (fonte unica), entao os numeros
              batem com os cartoes de la — descontados os sem telefone. */}
          {/* Uma linha so' com rolagem: chips quebrando em duas linhas viravam
              um bloco confuso entre as abas e a busca. */}
          <div className="mb-3 flex gap-2 overflow-x-auto pb-1 [-webkit-overflow-scrolling:touch]">
            <button onClick={() => setFiltros([])}
              className={`shrink-0 rounded-full border px-3 py-1 text-xs transition-colors ${
                !filtros.length ? "bg-primary text-primary-foreground border-primary" : "hover:border-primary"}`}>
              Todos <span className="opacity-70 tabular-nums">({publico.length})</span>
            </button>
            {segmentos.map((s) => (
              <button key={s.chave}
                onClick={() => setFiltros((a) => a.includes(s.chave) ? a.filter((c) => c !== s.chave) : [...a, s.chave])}
                className={`shrink-0 rounded-full border px-3 py-1 text-xs transition-colors ${
                  filtros.includes(s.chave) ? "bg-primary text-primary-foreground border-primary" : "hover:border-primary"}`}>
                {s.label} <span className="opacity-70 tabular-nums">({s.alcancaveis})</span>
              </button>
            ))}
          </div>
          <div className="relative mb-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={busca} onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por nome ou telefone"
              className="w-full rounded-lg border bg-background pl-9 pr-3 py-2 text-sm" />
          </div>
          <div className="rounded-lg border overflow-hidden">
            <div className="flex items-center justify-between border-b bg-muted/50 backdrop-blur px-3 py-2 sticky top-0 z-10">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={todosMarcados} onChange={alternarTodos} className="rounded border-input" />
                Selecionar todos
              </label>
              <span className="text-xs text-muted-foreground tabular-nums">
                {selecionados.size} de {lista.length}
              </span>
            </div>
            {!lista.length ? (
              <div className="py-12 text-center">
                <Users className="w-8 h-8 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-sm font-medium">Nenhum cliente encontrado</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {publico.length ? "Ajuste os filtros ou a busca."
                    : "Os clientes aparecem aqui conforme fazem pedidos com telefone cadastrado."}
                </p>
              </div>
            ) : (
              <div className="max-h-64 overflow-y-auto divide-y relative">
                {lista.slice(0, 300).map((p) => (
                  <label key={p.cliente_id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/30 cursor-pointer">
                    <input type="checkbox" checked={selecionados.has(p.cliente_id)}
                      onChange={() => setSelecionados((s) => {
                        const n = new Set(s); n.has(p.cliente_id) ? n.delete(p.cliente_id) : n.add(p.cliente_id); return n;
                      })} className="rounded border-input" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{p.nome}</p>
                      <p className="text-xs text-muted-foreground tabular-nums">{fone(p.telefone)}</p>
                    </div>
                    <span className="text-[11px] rounded-full bg-muted px-2 py-0.5 text-muted-foreground shrink-0">
                      {segmentos.find((s) => s.chave === p.segmento)?.label || p.segmento}
                    </span>
                  </label>
                ))}
                {lista.length > 300 && (
                  <p className="px-3 py-2 text-xs text-muted-foreground">
                    Mostrando 300 de {lista.length}. "Selecionar todos" marca só os visíveis.
                  </p>
                )}
                {listaBloqueados.map((p) => (
                  <div key={p.cliente_id || p.telefone}
                    className="flex items-center gap-3 px-3 py-2.5 opacity-60 cursor-not-allowed select-none"
                    title={p.motivo === "optout"
                      ? "Este cliente enviou PARE e não pode receber campanhas"
                      : "Já recebeu 7 mensagens nos últimos 7 dias — limite legal"}>
                    <Ban className="w-4 h-4 text-red-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate line-through decoration-muted-foreground/60">{p.nome}</p>
                      <p className="text-xs text-muted-foreground tabular-nums">{fone(p.telefone)}</p>
                    </div>
                    <span className="text-[11px] rounded-full bg-red-500/10 text-red-500 px-2 py-0.5 shrink-0 font-medium">
                      {p.motivo === "optout" ? "Pediu para sair" : "Limite semanal"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {aba === "csv" && (
        <div className="rounded-lg border border-dashed p-6 text-center">
          <Upload className="w-8 h-8 mx-auto text-muted-foreground/50 mb-3" />
          <input type="file" accept=".csv,.txt" id="sms-csv" className="hidden"
            onChange={(e) => e.target.files?.[0] && lerCsv(e.target.files[0])} />
          <label htmlFor="sms-csv"
            className="inline-block rounded-lg border px-4 py-2 text-sm font-medium cursor-pointer hover:border-primary">
            Escolher arquivo
          </label>
          <p className="text-xs text-muted-foreground mt-3">
            Aceita CSV ou TXT. Procuro telefones em qualquer coluna — não precisa formatar.
          </p>
        </div>
      )}

      {aba === "manual" && (
        <div>
          <textarea value={rascunho} onChange={(e) => setRascunho(e.target.value)} rows={3}
            placeholder="Cole ou digite os números, um por linha ou separados por vírgula"
            className="w-full rounded-lg border bg-background px-3 py-2 text-sm" />
          <button onClick={() => adicionarNumeros(rascunho)} disabled={!rascunho.trim()}
            className="mt-2 rounded-lg border px-4 py-2 text-sm font-medium disabled:opacity-40">
            Adicionar
          </button>
        </div>
      )}

      {avulsos.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium">{avulsos.length} número(s) de fora da base</p>
            <button onClick={() => setAvulsos([])} className="text-xs text-muted-foreground hover:text-foreground">
              limpar
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
            {avulsos.map((t) => (
              <span key={t} className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs tabular-nums">
                {fone(t)}
                <button onClick={() => setAvulsos((a) => a.filter((x) => x !== t))}
                  className="text-muted-foreground hover:text-foreground"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        </div>
      )}

      {precisaConsentimento && (
        <label className="mt-4 flex items-start gap-2.5 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 cursor-pointer">
          <input type="checkbox" checked={consentimento} onChange={(e) => setConsentimento(e.target.checked)}
            className="mt-0.5 rounded border-input" />
          <span className="text-xs">
            <span className="flex items-center gap-1.5 font-medium text-amber-500 mb-1">
              <ShieldAlert className="w-3.5 h-3.5" /> Confirmação obrigatória
            </span>
            <span className="text-muted-foreground">
              Declaro que estas pessoas autorizaram a loja a enviar mensagens. Fica registrado
              quem confirmou e quando. Enviar para lista comprada ou sem autorização é infração
              da LGPD, e a responsabilidade é de quem dispara.
            </span>
          </span>
        </label>
      )}

      {funil && (
        <div className="mt-3 text-xs text-muted-foreground space-y-0.5">
          {funil.sem_telefone > 0 && <p>{funil.sem_telefone} sem telefone, fora da conta.</p>}
          {funil.telefone_invalido > 0 && <p>{funil.telefone_invalido} com telefone inválido, fora da conta.</p>}
          {funil.duplicados > 0 && <p>{funil.duplicados} número repetido, contado uma vez só.</p>}
          {funil.no_optout > 0 && <p>{funil.no_optout} pediram para não receber e foram removidos.</p>}
          {funil.no_teto_semanal > 0 && (
            <p>{funil.no_teto_semanal} já receberam 7 mensagens nos últimos 7 dias — o limite legal.</p>
          )}
        </div>
      )}
    </div>
  );
}
